import { useSnackbar } from '@hue/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  getApiError,
  schedulingApi,
  type Appointment,
  type AppointmentConfirmation,
  type AppointmentStatus,
} from '../../lib/api.js';

/// Agendado e confirmado ainda mudam: confirmam, remarcam, cancelam. Concluído e cancelado não
/// (ADR 0030 e ADR 0036).
export function isLive(status: AppointmentStatus): boolean {
  return status === 'SCHEDULED' || status === 'CONFIRMED';
}

function confirmedMessage(result: AppointmentConfirmation, opened: boolean): string {
  if (result.whatsappUrl && !opened) {
    return 'Agendamento confirmado, mas o navegador bloqueou a janela do WhatsApp. Libere pop-ups deste endereço e use "Reenviar confirmação".';
  }
  if (result.whatsappUrl && result.emailQueued) {
    return 'Agendamento confirmado. WhatsApp aberto com a mensagem pronta, e o e-mail está na fila.';
  }
  if (result.whatsappUrl) return 'Agendamento confirmado. WhatsApp aberto com a mensagem pronta.';
  if (result.emailQueued) {
    return 'Agendamento confirmado. Cliente sem celular cadastrado: o aviso foi por e-mail.';
  }
  return 'Agendamento confirmado. Cliente sem celular nem e-mail cadastrado: avise por outro meio.';
}

/// A empresa confirma o agendamento e avisa o cliente, do mesmo jeito na lista e no calendário.
/// Confirmar de novo reenvia o aviso.
export function useAppointmentConfirmation(onChanged?: (appointment: Appointment) => void) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [pending, setPending] = useState<string | null>(null);

  async function confirm(appointment: Pick<Appointment, 'id'>) {
    // A janela abre já no clique: aberta só depois da resposta da API, o navegador a trataria como
    // pop-up e a bloquearia. Sem endereço, ela fecha.
    const tab = window.open('', '_blank');
    setPending(appointment.id);
    try {
      const result = await schedulingApi.confirmAppointment(appointment.id);
      if (tab && result.whatsappUrl) {
        // A conversa não precisa alcançar a agenda pela janela que a abriu.
        tab.opener = null;
        tab.location.href = result.whatsappUrl;
      } else {
        tab?.close();
      }
      // Confirmar também pode liberar sessões e converter o sinal de outros pedidos em crédito.
      // Atualiza as leituras ativas da operação, inclusive a carteira mostrada na própria Agenda.
      await queryClient.invalidateQueries({ queryKey: ['company'] });
      onChanged?.(result.appointment);
      snackbar.success(confirmedMessage(result, Boolean(tab)));
    } catch (cause) {
      tab?.close();
      snackbar.failure(getApiError(cause));
    } finally {
      setPending(null);
    }
  }

  return { confirm, pending };
}
